// apps/client/src/hooks/useOrganisationProfile.ts

import { useState, useEffect } from 'react';
import type { OrganisationProfile, OrganisationProfileData } from '@fsp/types';
import {
  fetchOrganisationProfile,
  updateOrganisationProfile,
} from '../services/organisationProfile';

/**
 * Hook to manage organisation AI profile
 */
export const useOrganisationProfile = (organisationId: string | null) => {
  const [profile, setProfile] = useState<OrganisationProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organisationId) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const loadProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchOrganisationProfile(organisationId, controller.signal);
        setProfile(data);
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Failed to load profile');
        }
      } finally {
        setLoading(false);
      }
    };

    loadProfile();

    return () => {
      controller.abort();
    };
  }, [organisationId]);

  const updateProfile = async (profileData: OrganisationProfileData): Promise<boolean> => {
    if (!organisationId) return false;

    try {
      setError(null);
      const updated = await updateOrganisationProfile(organisationId, profileData);
      setProfile(updated);
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
      return false;
    }
  };

  const refreshProfile = async () => {
    if (!organisationId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await fetchOrganisationProfile(organisationId);
      setProfile(data);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh profile');
    } finally {
      setLoading(false);
    }
  };

  return {
    profile,
    loading,
    error,
    updateProfile,
    refreshProfile,
    hasProfile: profile !== null,
  };
};
